import * as React from 'react';
import { Button, Chip, CircularProgress, Divider, IconButton, Link as MuiLink, Stack, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { getRoute } from '../../app/router';
import { ArrowsClockwise, FunnelSimple, Trash } from '@phosphor-icons/react';
import {
  DataGrid,
  DataGridColumn,
  FilterBadges,
  ListPageWrapper,
  PageTitle,
} from '../../shared';
import { withConversionSyncFilterDrawer, useConversionSyncFilterDrawer } from './conversion-sync-filter-drawer';
import {
  OfflineConversionSyncProcessesQuery,
  useDeleteOfflineConversionSyncProcessMutation,
  useOfflineConversionSyncProcessesQuery,
  useSyncOfflineConversionsMutation,
} from '@coverright/data-access/enrollment';
import { enrollmentApolloClient } from '@coverright/data-access/apollo-clients';
import { EventName, OfflineConversionSyncProcessFilterInput, Platform } from '@coverright/data-access/types/enrollment';
import moment from 'moment';
import _ from 'lodash';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';

type Row = OfflineConversionSyncProcessesQuery['offlineConversionSyncProcesses']['data'][0];

const PAGE_SIZE = 20;

const Page = () => {
  const [page, setPage] = React.useState(0);
  const [filters, setFilters] = React.useState<OfflineConversionSyncProcessFilterInput>({});
  const { open } = useConversionSyncFilterDrawer();
  const [loading, setLoading] = useState(false);

  const { data, refetch } = useOfflineConversionSyncProcessesQuery({
    variables: { filter: filters, page: { page, size: PAGE_SIZE } },
    client: enrollmentApolloClient,
  });

  const [syncMutation] = useSyncOfflineConversionsMutation({
    client: enrollmentApolloClient,
    onCompleted: () => refetch(),
  });

  const [deleteMutation] = useDeleteOfflineConversionSyncProcessMutation({
    client: enrollmentApolloClient,
    onCompleted: () => refetch(),
  });

  const rows = data?.offlineConversionSyncProcesses?.data ?? [];
  const total = data?.offlineConversionSyncProcesses?.totalElements ?? 0;

  const openFilters = () => open(f => f && setFilters(f), filters);

  const onFilterRemoveClick = (key: keyof OfflineConversionSyncProcessFilterInput) => {
    setFilters(prev => {
      setPage(0);
      return _.omit(prev, key) as OfflineConversionSyncProcessFilterInput;
    });
  };

  const handleSyncClick = () => {
    setLoading(true);
    syncMutation()
    setTimeout(() => {
      refetch()
      setLoading(false)
    }, 10000)
  }

  return (
    <ListPageWrapper>
      <PageTitle title={'Conversion Sync'}>
        <Stack direction={'row'} gap={2}>
          <LoadingButton
            loading={loading}
            variant={'outlined'}
            size={'small'}
            startIcon={<ArrowsClockwise weight={'bold'} />}
            disabled={loading}
            onClick={handleSyncClick}
          >
            Sync now
          </LoadingButton>
          <Divider orientation={'vertical'} />
          <Button
            variant={'outlined'}
            size={'small'}
            startIcon={<FunnelSimple weight={'bold'} />}
            onClick={openFilters}
          >
            Filters
          </Button>
        </Stack>
      </PageTitle>

      <FilterBadges<OfflineConversionSyncProcessFilterInput>
        filter={filters}
        onRemoveFilterClick={onFilterRemoveClick}
        nameMapFunc={filterNameFunc}
      />

      <DataGrid<Row, never>
        columns={makeColumns(id => deleteMutation({ variables: { id } }))}
        rows={rows}
        loading={loading}
        page={page}
        rowsPerPage={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />
    </ListPageWrapper>
  );
};

export const ConversionSync = withConversionSyncFilterDrawer(Page);

const filterNameFunc = (name: keyof OfflineConversionSyncProcessFilterInput): string => {
  switch (name) {
    case 'createdAtFrom': return 'Created from';
    case 'createdAtTo': return 'Created to';
    case 'eventName': return 'Event';
    case 'platform': return 'Platform';
    case 'synced': return 'Synced';
    case 'hasError': return 'Has error';
    default: return name;
  }
};

const AttributesCell = ({ row }: { row: Row }) => {
  const attrs: { label: string; value: string | null | undefined }[] = [
    { label: 'UID', value: row.uId },
    { label: 'CID', value: row.cId },
    { label: 'GCLID', value: row.gclid },
    { label: 'FBCLID', value: row.fbclid },
    { label: 'GBRAID', value: row.gbraid },
    { label: 'WBRAID', value: row.wbraid },
    { label: 'MSCLKID', value: row.msclkid },
    { label: 'UTM source', value: row.utmSource },
    { label: 'UTM medium', value: row.utmMedium },
    { label: 'UTM campaign', value: row.utmCampaign },
    { label: 'UTM brand', value: row.utmBrand },
  ].filter(a => !!a.value);

  if (!attrs.length) return <>—</>;

  return (
    <Tooltip
      title={
        <Stack gap={0.5} sx={{ p: 0.5 }}>
          {attrs.map(a => (
            <Stack key={a.label} direction={'row'} gap={1}>
              <span style={{ opacity: 0.7, minWidth: 90 }}>{a.label}:</span>
              <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{a.value}</span>
            </Stack>
          ))}
        </Stack>
      }
      placement={'left'}
    >
      <span style={{ cursor: 'default', color: '#1976d2', textDecoration: 'underline' }}>View</span>
    </Tooltip>
  );
};

const fmt = (v: unknown) =>
  v ? moment(String(v)).format('MM/DD/YYYY HH:mm') : '—';

const eventNameLabel: Record<EventName, string> = {
  [EventName.Lead]: 'Lead',
  [EventName.Sql]: 'SQL',
  [EventName.FollowUp]: 'Follow-up',
};

const platformLabel: Record<Platform, string> = {
  [Platform.GoogleCoverright]: 'Google CR',
  [Platform.GoogleCaliburn]: 'Google Cal',
  [Platform.MetaCoverright]: 'Meta CR',
  [Platform.MicrosoftCoverright]: 'Microsoft CR',
  [Platform.MicrosoftCaliburn]: 'Microsoft Cal',
};

const makeColumns = (onDelete: (id: any) => void): DataGridColumn<Row, never>[] => [
  {
    title: 'Conversion time',
    value: r => fmt(r.conversionTime),
    width: 150,
  },
  {
    title: 'Client ID',
    value: r => (
      <MuiLink component={Link}
               color={'text.primary'}
               to={`/${getRoute('client')}/${r.clientId}`}>
        {r.clientId}
      </MuiLink>
    ),
    width: 200,
  },
  {
    title: 'Event',
    value: r => eventNameLabel[r.eventName] ?? r.eventName,
    width: 100,
  },
  {
    title: 'Platform',
    value: r => r.platform ? platformLabel[r.platform] ?? r.platform : '—',
    width: 130,
  },
  {
    title: 'Synced',
    value: r => (
      <Chip
        size={'small'}
        label={r.synced ? 'Synced' : 'Pending'}
        color={r.synced ? 'success' : 'default'}
        variant={'outlined'}
      />
    ),
    width: 90,
  },
  {
    title: 'Error',
    value: r => r.errorMsg ? (
      <Tooltip title={r.errorMsg}>
        <Chip size={'small'} label={'Error'} color={'error'} variant={'outlined'} />
      </Tooltip>
    ) : '—',
    width: 80,
  },
  {
    title: 'Attributes',
    value: r => <AttributesCell row={r} />,
    width: 80,
  },
  {
    title: '',
    value: r => (
      <Tooltip title={'Delete'}>
        <IconButton size={'small'} color={'error'} onClick={() => onDelete(r.id)}>
          <Trash size={16} />
        </IconButton>
      </Tooltip>
    ),
    width: 48,
  },
];
